/**
 * Internationalization (i18n) System
 * Supports English and Chinese Simplified
 */

// Translation data
const translations = {
  en: {
    // Header
    'app.title': 'mHYPER Risk Monitor',
    'app.subtitle': 'Real-time monitoring for Hyperithm USDC strategy',
    'app.lastUpdated': 'Last Updated',
    'app.staleData': '⚠ Stale Data',
    'app.refresh': 'Refresh',
    'app.refreshing': 'Refreshing...',
    'app.settings': 'Settings',

    // Risk Gauge
    'risk.compositeScore': 'Composite Risk Score',

    // Portfolio Form
    'portfolio.title': 'Portfolio Configuration',
    'portfolio.totalValue': 'Total Portfolio Value (USD)',
    'portfolio.totalValueDesc': 'Your total portfolio value across all positions',
    'portfolio.exposure': 'mHYPER Exposure',
    'portfolio.exposureAmount': 'Exposure Amount:',
    'portfolio.vaultDiversification': 'Vault Diversification',
    'portfolio.vaults': 'vaults',
    'portfolio.exposureGreen': '✓ Exposure within recommended range (<10%)',
    'portfolio.exposureYellow': '⚠ Moderate exposure (10-20%). Consider diversification.',
    'portfolio.exposureRed': '🚨 High exposure (>20%). Strongly recommend reducing position.',
    'portfolio.diversified': '✓ Well diversified across multiple vaults',
    'portfolio.diversify': '⚠ Consider diversifying across 3+ vaults',

    // Recommendations
    'portfolio.recommendations': '💡 Recommendations',
    'portfolio.rec1': '• Keep mHYPER exposure below 5-10% of total portfolio',
    'portfolio.rec2': '• Diversify across 3+ vaults (e.g., Gauntlet USDC, Steakhouse)',
    'portfolio.rec3': '• Monitor utilization and exit if conditions deteriorate',
    'portfolio.rec4': '• Consider insurance coverage via Nexus Mutual',

    // Quick Actions
    'portfolio.conservative': 'Conservative',
    'portfolio.moderate': 'Moderate',
    'portfolio.aggressive': 'Aggressive',

    // Metrics
    'metrics.utilization': 'Utilization Rate',
    'metrics.utilizationDesc': 'Percentage of vault assets currently borrowed',
    'metrics.peg': 'mHYPER Peg',
    'metrics.pegDesc': 'Deviation from $1 USDC peg',
    'metrics.loopExposure': 'Loop Exposure',
    'metrics.loopExposureDesc': 'Percentage of TVL from recursive deposits',
    'metrics.fundingRate': 'Funding Rate',
    'metrics.fundingRateDesc': '8h average funding rate for key perps',
    'metrics.tvlChange': 'TVL Change (24h)',
    'metrics.tvlChangeDesc': '24-hour total value locked change',
    'metrics.tvlCurrent': 'Current TVL',
    'metrics.tvlCurrentDesc': 'Total value locked in Hyperithm vaults',

    // Thresholds
    'thresholds.utilization': 'Red: >90%, Yellow: 80-90%, Green: <80%',
    'thresholds.peg': 'Red: >1%, Yellow: 0.5-1%, Green: <0.5%',
    'thresholds.loopExposure': 'Red: >40%, Yellow: 30-40%, Green: <30%',
    'thresholds.fundingRate': 'Red: <-0.5%, Yellow: -0.5-0%, Green: >0%',
    'thresholds.tvlChange': 'Red: <0%, Yellow: 0-1%, Green: >1%',

    // Recommended Action
    'action.recommended': '📋 Recommended Action',
    'action.detailedAnalysis': 'Detailed Risk Analysis',
    'action.riskSummary': 'Risk Summary',
    'action.compositeScore': 'Composite Score',
    'action.breachedMetrics': 'Breached Metrics',
    'action.recommendedSteps': 'Recommended Steps',
    'action.step1': 'Continue monitoring current positions',
    'action.step2': 'Maintain current risk management strategy',
    'action.step3': 'Review position sizing and consider reducing exposure',
    'action.step4': 'Monitor metrics more closely for further deterioration',
    'action.step5': 'Consider setting stop-loss orders or exit triggers',
    'action.step6': 'IMMEDIATE ACTION: Reduce position to <5% of portfolio',
    'action.step7': 'Consider exiting positions entirely if conditions worsen',
    'action.step8': 'Move funds to diversified vaults (e.g., Gauntlet USDC)',
    'action.portfolioContext': 'Portfolio Context',

    // Vault Allocations
    'vaults.title': 'Vault Allocations',
    'vaults.totalVaults': 'Total Vaults',
    'vaults.allocation': 'Allocation',
    'vaults.tvl': 'TVL',
    'vaults.utilization': 'Utilization',
    'vaults.apy': 'APY',
    'vaults.status': 'Status',
    'vaults.statusHigh': 'High',
    'vaults.statusMedium': 'Medium',
    'vaults.statusLow': 'Low',
    'vaults.riskLow': 'Low Risk',
    'vaults.riskMedium': 'Medium Risk',
    'vaults.riskHigh': 'High Risk',
    'vaults.riskUnknown': 'Unknown',
    'vaults.totalTvl': 'Total TVL',
    'vaults.avgUtilization': 'Avg Utilization',
    'vaults.avgApy': 'Avg APY',
    'vaults.highRiskVaults': 'High Risk',
    'vaults.currentApy': 'Current APY',
    'vaults.depositSummary': 'Deposit Summary',
    'vaults.depositUsdc': 'Deposit (USDC)',
    'vaults.monthlyEarnings': 'Projected monthly earnings',
    'vaults.yearlyEarnings': 'Projected yearly earnings',
    'vaults.historicalApy': 'Historical APY',
    'vaults.apyRange': 'APY Range',
    'vaults.marketExposure': 'Market Exposure',
    'vaults.totalMarkets': 'Total Markets',
    'vaults.healthScore': 'Health Score',
    'vaults.vaultAllocation': 'Vault Allocation',
    'vaults.vaultAllocationUsd': 'Vault Allocation ($)',
    'vaults.supplyCap': 'Supply Cap',
    'vaults.showing': 'Showing',
    'vaults.of': 'of',

    // About Section
    'about.title': 'ℹ️ About This Dashboard',
    'about.desc1': 'This dashboard monitors key risk metrics for the Hyperithm mHYPER USDC strategy in real-time.',
    'about.desc2': 'Data Sources: Morpho Blue (utilization), DexScreener (peg), Etherscan (loop exposure), CoinGlass (funding rates), DefiLlama (TVL).',
    'about.desc3': 'Update Frequency: Metrics refresh every {interval}s. Historical data is stored locally for trend analysis.',
    'about.desc4': 'Rate Limiting: All APIs use free tier limits. Dashboard automatically manages rate limits and falls back to simulated data when necessary.',
    'about.desc5': 'Privacy: All data processing happens in your browser. No data is sent to external servers.',

    // Settings
    'settings.title': 'Settings',
    'settings.general': 'General',
    'settings.rateLimits': 'API Rate Limits',
    'settings.polling': 'Polling Settings',
    'settings.data': 'Data Management',
    'settings.refreshInterval': 'Refresh Interval (seconds)',
    'settings.refreshIntervalDesc': 'How often to poll for new data (30-600 seconds). Recommended: 60 seconds.',
    'settings.stalenessThreshold': 'Data Staleness Threshold (minutes)',
    'settings.stalenessThresholdDesc': 'When to show "stale data" warning (1-60 minutes)',
    'settings.optimizationTips': '💡 Optimization Tips',
    'settings.tip1': '• 60-second intervals provide good balance of freshness vs API usage',
    'settings.tip2': '• Higher intervals reduce API calls but may miss rapid changes',
    'settings.tip3': '• Monitor rate limits to avoid hitting API quotas',
    'settings.resetAll': 'Reset All Counters',
    'settings.dailyUsage': 'Daily Usage',
    'settings.perMinute': 'Per-minute',
    'settings.resetsIn': 'Resets in',
    'settings.used': 'used',
    'settings.warnings': '⚠️ Rate Limit Warnings',
    'settings.warningDesc': 'When rate limits are reached, the app will use cached data and display fallback values. This ensures continuous monitoring even when APIs are unavailable.',
    'settings.pollingIntervals': 'API Polling Intervals',
    'settings.interval': 'Interval:',
    'settings.minutes': 'minutes',
    'settings.nextFetch': 'Next fetch:',
    'settings.availableNow': 'Available now',
    'settings.in': 'in',
    'settings.ready': 'Ready',
    'settings.waiting': 'Waiting',
    'settings.dailyProjections': 'Daily Usage Projections',
    'settings.projectionDesc': 'Intervals are calculated to use only 10% of daily limits to avoid waste',
    'settings.dailyLimitPolling': 'Daily Limit Based Polling',
    'settings.dailyLimitDesc': 'All polling intervals are calculated to use only 10% of daily API limits. This conservative approach ensures we never waste API quotas while maintaining reasonable data freshness.',
    'settings.historicalData': 'Historical Data',
    'settings.historicalDesc': 'Historical data is stored locally in your browser for trend analysis and charting.',
    'settings.utilizationHistory': 'Utilization History',
    'settings.priceHistory': 'Price History',
    'settings.tvlHistory': 'TVL History',
    'settings.storedLocally': 'Stored locally',
    'settings.clearAllData': 'Clear All Data',
    'settings.clearWarning': 'This will permanently delete all historical data, preferences, and cached API responses.',
    'settings.clearButton': 'Clear All Data & Reset',

    // Rate Limits
    'rateLimits.title': 'API Rate Limit Status',
    'rateLimits.resetAll': 'Reset All Counters',
    'rateLimits.dailyUsage': 'Daily Usage',
    'rateLimits.perMinute': 'Per-minute',
    'rateLimits.resetsIn': 'Resets in',
    'rateLimits.warnings': '⚠️ Rate Limit Warnings',
    'rateLimits.warningDesc': 'When rate limits are reached, the app will use cached data and display fallback values. This ensures continuous monitoring even when APIs are unavailable.',

    // Polling Settings
    'polling.title': 'API Polling Intervals',
    'polling.ready': 'Ready',
    'polling.waiting': 'Waiting',
    'polling.interval': 'Interval:',
    'polling.nextFetch': 'Next fetch:',
    'polling.availableNow': 'Available now',
    'polling.optimized': '✅ Optimized Polling',
    'polling.optimizedDesc': 'Polling intervals are optimized to balance data freshness with API rate limits. Critical data (utilization, price) updates more frequently than historical data (TVL, funding rates).',

    // Data Management
    'data.title': 'Data Management',
    'data.historical': 'Historical Data',
    'data.historicalDesc': 'Historical data is stored locally in your browser for trend analysis and charting.',
    'data.utilizationHistory': 'Utilization History',
    'data.priceHistory': 'Price History',
    'data.tvlHistory': 'TVL History',
    'data.storedLocally': 'Stored locally',
    'data.clearAll': '🗑️ Clear All Data',
    'data.clearDesc': 'This will permanently delete all historical data, preferences, and cached API responses.',
    'data.clearButton': 'Clear All Data & Reset',

    // API Status
    'apiStatus.title': 'API Status',
    'apiStatus.allGood': 'All Good',
    'apiStatus.nearLimit': 'Near Limit',
    'apiStatus.rateLimited': 'Rate Limited',
    'apiStatus.nextRefresh': 'Next refresh:',
    'apiStatus.ready': 'Ready',

    // Daily Usage Projections
    'projections.title': '📊 Daily Usage Projections',
    'projections.desc': 'Intervals are calculated to use only 10% of daily limits to avoid waste',
    'projections.conservative': '✅ Daily Limit Based Polling',
    'projections.conservativeDesc': 'All polling intervals are calculated to use only 10% of daily API limits. This conservative approach ensures we never waste API quotas while maintaining reasonable data freshness.',

    // Status indicators
    'status.red': 'RED',
    'status.yellow': 'YELLOW', 
    'status.green': 'GREEN',
    'status.stable': 'Stable',
    'status.falling': 'Falling',
    'status.rising': 'Rising',
    'status.threshold': 'Threshold',

    // Risk levels
    'risk.low': 'Low Risk',
    'risk.moderate': 'Moderate Risk',
    'risk.high': 'High Risk',
    'risk.caution': 'CAUTION',
    'risk.warning': 'Elevated risk detected. Review positions and consider risk mitigation.',
    'risk.normal': 'Risk levels normal. Continue monitoring.',
    'risk.critical': '⚠️ HIGH RISK: {count}+ metrics breached. IMMEDIATE ACTION: Reduce position to <5% of portfolio or exit entirely. Consider moving funds to diversified vaults (e.g., Gauntlet USDC).',

    // Portfolio form - missing translations
    'portfolio.exposureLabel': 'mHYPER Exposure',
    'portfolio.exposureAmount': 'Exposure Amount:',
    'portfolio.exposureGreen': 'Exposure within recommended range (<10%)',
    'portfolio.vaultDiversification': 'Vault Diversification',
    'portfolio.diversified': 'Well diversified across multiple vaults',
    'portfolio.rec1': 'Keep mHYPER exposure below 5-10% of total portfolio',
    'portfolio.rec2': 'Diversify across 3+ vaults (e.g., Gauntlet USDC, Steakhouse)',
    'portfolio.rec3': 'Monitor utilization and exit if conditions deteriorate',
    'portfolio.rec4': 'Consider insurance coverage via Nexus Mutual',
    'portfolio.quickSetup': 'Quick Setup',
    'portfolio.quickHelp': 'Click to quickly set exposure and vault count',

    // API Status
    'apiStatus.title': 'API Status',
    'apiStatus.rateLimited': 'Rate Limited',
    'apiStatus.nextRefresh': 'Next refresh:',
    'apiStatus.moreApis': 'more APIs',

    // Alert messages
    'alert.moderateRisk': 'Moderate risk detected ({count} metric(s) elevated). Review position sizing and consider partial exit if conditions worsen.',

    // Common
    'common.loading': 'Loading metrics...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.warning': 'Warning',
    'common.info': 'Info'
  },
  zh: {
    // Header
    'app.title': 'mHYPER 风险监控器',
    'app.subtitle': 'Hyperithm USDC 策略实时监控',
    'app.lastUpdated': '最后更新',
    'app.staleData': '⚠ 数据过期',
    'app.refresh': '刷新',
    'app.refreshing': '刷新中...',
    'app.settings': '设置',

    // Risk Gauge
    'risk.compositeScore': '综合风险评分',

    // Portfolio Form
    'portfolio.title': '投资组合配置',
    'portfolio.totalValue': '投资组合总价值 (USD)',
    'portfolio.totalValueDesc': '您所有持仓的总投资组合价值',
    'portfolio.exposure': 'mHYPER 敞口',
    'portfolio.exposureAmount': '敞口金额:',
    'portfolio.vaultDiversification': '金库多样化',
    'portfolio.vaults': '个金库',
    'portfolio.exposureGreen': '✓ 敞口在推荐范围内 (<10%)',
    'portfolio.exposureYellow': '⚠ 中等敞口 (10-20%)。考虑多样化。',
    'portfolio.exposureRed': '🚨 高敞口 (>20%)。强烈建议减少持仓。',
    'portfolio.diversified': '✓ 在多个金库中良好多样化',
    'portfolio.diversify': '⚠ 考虑在3个以上金库中多样化',

    // Recommendations
    'portfolio.recommendations': '💡 建议',
    'portfolio.rec1': '• 将 mHYPER 敞口保持在总投资组合的 5-10% 以下',
    'portfolio.rec2': '• 在3个以上金库中多样化（如 Gauntlet USDC、Steakhouse）',
    'portfolio.rec3': '• 监控利用率，如果条件恶化则退出',
    'portfolio.rec4': '• 考虑通过 Nexus Mutual 购买保险',

    // Quick Actions
    'portfolio.conservative': '保守',
    'portfolio.moderate': '中等',
    'portfolio.aggressive': '激进',

    // Metrics
    'metrics.utilization': '利用率',
    'metrics.utilizationDesc': '当前借出的金库资产百分比',
    'metrics.peg': 'mHYPER 锚定',
    'metrics.pegDesc': '与 $1 USDC 锚定的偏差',
    'metrics.loopExposure': '循环敞口',
    'metrics.loopExposureDesc': '来自递归存款的 TVL 百分比',
    'metrics.fundingRate': '资金费率',
    'metrics.fundingRateDesc': '主要永续合约的8小时平均资金费率',
    'metrics.tvlChange': 'TVL 变化 (24h)',
    'metrics.tvlChangeDesc': '24小时总锁定价值变化',
    'metrics.tvlCurrent': '当前 TVL',
    'metrics.tvlCurrentDesc': 'Hyperithm 金库中的总锁定价值',

    // Thresholds
    'thresholds.utilization': '红色: >90%, 黄色: 80-90%, 绿色: <80%',
    'thresholds.peg': '红色: >1%, 黄色: 0.5-1%, 绿色: <0.5%',
    'thresholds.loopExposure': '红色: >40%, 黄色: 30-40%, 绿色: <30%',
    'thresholds.fundingRate': '红色: <-0.5%, 黄色: -0.5-0%, 绿色: >0%',
    'thresholds.tvlChange': '红色: <0%, 黄色: 0-1%, 绿色: >1%',

    // Recommended Action
    'action.recommended': '📋 推荐操作',
    'action.detailedAnalysis': '详细风险分析',
    'action.riskSummary': '风险摘要',
    'action.compositeScore': '综合评分',
    'action.breachedMetrics': '违规指标',
    'action.recommendedSteps': '推荐步骤',
    'action.step1': '继续监控当前持仓',
    'action.step2': '维持当前风险管理策略',
    'action.step3': '审查持仓规模并考虑减少敞口',
    'action.step4': '更密切地监控指标以观察进一步恶化',
    'action.step5': '考虑设置止损订单或退出触发器',
    'action.step6': '立即行动：将持仓减少至投资组合的 <5%',
    'action.step7': '如果条件恶化，考虑完全退出持仓',
    'action.step8': '将资金转移到多样化金库（如 Gauntlet USDC）',
    'action.portfolioContext': '投资组合背景',

    // Vault Allocations
    'vaults.title': '金库分配',
    'vaults.totalVaults': '总金库数',
    'vaults.allocation': '分配比例',
    'vaults.tvl': '总锁定价值',
    'vaults.utilization': '利用率',
    'vaults.apy': '年化收益率',
    'vaults.status': '状态',
    'vaults.statusHigh': '高',
    'vaults.statusMedium': '中',
    'vaults.statusLow': '低',
    'vaults.riskLow': '低风险',
    'vaults.riskMedium': '中等风险',
    'vaults.riskHigh': '高风险',
    'vaults.riskUnknown': '未知',
    'vaults.totalTvl': '总锁定价值',
    'vaults.avgUtilization': '平均利用率',
    'vaults.avgApy': '平均年化收益率',
    'vaults.highRiskVaults': '高风险',
    'vaults.currentApy': '当前年化收益率',
    'vaults.depositSummary': '存款摘要',
    'vaults.depositUsdc': '存款 (USDC)',
    'vaults.monthlyEarnings': '预计月收益',
    'vaults.yearlyEarnings': '预计年收益',
    'vaults.historicalApy': '历史年化收益率',
    'vaults.apyRange': '年化收益率范围',
    'vaults.marketExposure': '市场敞口',
    'vaults.totalMarkets': '总市场数',
    'vaults.healthScore': '健康评分',
    'vaults.vaultAllocation': '金库分配',
    'vaults.vaultAllocationUsd': '金库分配 ($)',
    'vaults.supplyCap': '供应上限',
    'vaults.showing': '显示',
    'vaults.of': '共',

    // About Section
    'about.title': 'ℹ️ 关于此仪表板',
    'about.desc1': '此仪表板实时监控 Hyperithm mHYPER USDC 策略的关键风险指标。',
    'about.desc2': '数据源: Morpho Blue (利用率)、DexScreener (锚定)、Etherscan (循环敞口)、CoinGlass (资金费率)、DefiLlama (TVL)。',
    'about.desc3': '更新频率: 指标每 {interval} 秒刷新。历史数据本地存储用于趋势分析。',
    'about.desc4': '速率限制: 所有 API 使用免费层限制。仪表板自动管理速率限制，必要时回退到模拟数据。',
    'about.desc5': '隐私: 所有数据处理都在您的浏览器中进行。不会向外部服务器发送数据。',

    // Settings
    'settings.title': '设置',
    'settings.general': '常规',
    'settings.rateLimits': 'API 速率限制',
    'settings.polling': '轮询设置',
    'settings.data': '数据管理',
    'settings.refreshInterval': '刷新间隔 (秒)',
    'settings.refreshIntervalDesc': '轮询新数据的频率 (30-600 秒)。推荐: 60 秒。',
    'settings.stalenessThreshold': '数据过期阈值 (分钟)',
    'settings.stalenessThresholdDesc': '何时显示"数据过期"警告 (1-60 分钟)',
    'settings.optimizationTips': '💡 优化提示',
    'settings.tip1': '• 60秒间隔在新鲜度和API使用之间提供良好平衡',
    'settings.tip2': '• 更高间隔减少API调用但可能错过快速变化',
    'settings.tip3': '• 监控速率限制以避免达到API配额',
    'settings.resetAll': '重置所有计数器',
    'settings.dailyUsage': '每日使用',
    'settings.perMinute': '每分钟',
    'settings.resetsIn': '重置时间',
    'settings.used': '已使用',
    'settings.warnings': '⚠️ 速率限制警告',
    'settings.warningDesc': '当达到速率限制时，应用将使用缓存数据并显示回退值。这确保即使在API不可用时也能持续监控。',
    'settings.pollingIntervals': 'API 轮询间隔',
    'settings.interval': '间隔:',
    'settings.minutes': '分钟',
    'settings.nextFetch': '下次获取:',
    'settings.availableNow': '现在可用',
    'settings.in': '在',
    'settings.ready': '就绪',
    'settings.waiting': '等待中',
    'settings.dailyProjections': '每日使用预测',
    'settings.projectionDesc': '间隔计算为仅使用每日限制的10%以避免浪费',
    'settings.dailyLimitPolling': '基于每日限制的轮询',
    'settings.dailyLimitDesc': '所有轮询间隔都计算为仅使用每日API限制的10%。这种保守方法确保我们永远不会浪费API配额，同时保持合理的数据新鲜度。',
    'settings.historicalData': '历史数据',
    'settings.historicalDesc': '历史数据存储在您的浏览器本地，用于趋势分析和图表制作。',
    'settings.utilizationHistory': '利用率历史',
    'settings.priceHistory': '价格历史',
    'settings.tvlHistory': 'TVL 历史',
    'settings.storedLocally': '本地存储',
    'settings.clearAllData': '清除所有数据',
    'settings.clearWarning': '这将永久删除所有历史数据、偏好设置和缓存的API响应。',
    'settings.clearButton': '清除所有数据并重置',

    // Rate Limits
    'rateLimits.title': 'API 速率限制状态',
    'rateLimits.resetAll': '重置所有计数器',
    'rateLimits.dailyUsage': '每日使用',
    'rateLimits.perMinute': '每分钟',
    'rateLimits.resetsIn': '重置时间',
    'rateLimits.warnings': '⚠️ 速率限制警告',
    'rateLimits.warningDesc': '当达到速率限制时，应用将使用缓存数据并显示回退值。这确保即使在API不可用时也能持续监控。',

    // Polling Settings
    'polling.title': 'API 轮询间隔',
    'polling.ready': '就绪',
    'polling.waiting': '等待中',
    'polling.interval': '间隔:',
    'polling.nextFetch': '下次获取:',
    'polling.availableNow': '现在可用',
    'polling.optimized': '✅ 优化轮询',
    'polling.optimizedDesc': '轮询间隔经过优化，平衡数据新鲜度与API速率限制。关键数据（利用率、价格）比历史数据（TVL、资金费率）更新更频繁。',

    // Data Management
    'data.title': '数据管理',
    'data.historical': '历史数据',
    'data.historicalDesc': '历史数据本地存储在您的浏览器中，用于趋势分析和图表。',
    'data.utilizationHistory': '利用率历史',
    'data.priceHistory': '价格历史',
    'data.tvlHistory': 'TVL 历史',
    'data.storedLocally': '本地存储',
    'data.clearAll': '🗑️ 清除所有数据',
    'data.clearDesc': '这将永久删除所有历史数据、偏好设置和缓存的API响应。',
    'data.clearButton': '清除所有数据并重置',

    // API Status
    'apiStatus.title': 'API 状态',
    'apiStatus.allGood': '一切正常',
    'apiStatus.nearLimit': '接近限制',
    'apiStatus.rateLimited': '速率限制',
    'apiStatus.nextRefresh': '下次刷新:',
    'apiStatus.ready': '就绪',

    // Daily Usage Projections
    'projections.title': '📊 每日使用预测',
    'projections.desc': '间隔计算为仅使用每日限制的10%以避免浪费',
    'projections.conservative': '✅ 基于每日限制的轮询',
    'projections.conservativeDesc': '所有轮询间隔计算为仅使用每日API限制的10%。这种保守方法确保我们从不浪费API配额，同时保持合理的数据新鲜度。',

    // Status indicators
    'status.red': '红色',
    'status.yellow': '黄色', 
    'status.green': '绿色',
    'status.stable': '稳定',
    'status.falling': '下降',
    'status.rising': '上升',
    'status.threshold': '阈值',

    // Risk levels
    'risk.low': '低风险',
    'risk.moderate': '中等风险',
    'risk.high': '高风险',
    'risk.caution': '注意',
    'risk.warning': '检测到风险升高。请审查持仓并考虑风险缓解措施。',
    'risk.normal': '风险水平正常。继续监控。',
    'risk.critical': '⚠️ 高风险：{count}+ 个指标违规。立即行动：将持仓减少至投资组合的 <5% 或完全退出。考虑将资金转移到多样化金库（如 Gauntlet USDC）。',

    // Portfolio form - missing translations
    'portfolio.exposureLabel': 'mHYPER 敞口',
    'portfolio.exposureAmount': '敞口金额:',
    'portfolio.exposureGreen': '敞口在推荐范围内 (<10%)',
    'portfolio.vaultDiversification': '金库多样化',
    'portfolio.diversified': '在多个金库中良好多样化',
    'portfolio.rec1': '将 mHYPER 敞口保持在总投资组合的 5-10% 以下',
    'portfolio.rec2': '在3个以上金库中多样化（如 Gauntlet USDC、Steakhouse）',
    'portfolio.rec3': '监控利用率，如果条件恶化则退出',
    'portfolio.rec4': '考虑通过 Nexus Mutual 购买保险',
    'portfolio.quickSetup': '快速设置',
    'portfolio.quickHelp': '点击快速设置敞口和金库数量',

    // API Status
    'apiStatus.title': 'API 状态',
    'apiStatus.rateLimited': '速率限制',
    'apiStatus.nextRefresh': '下次刷新:',
    'apiStatus.moreApis': '更多 API',

    // Alert messages
    'alert.moderateRisk': '检测到中等风险（{count} 个指标升高）。请审查持仓规模，如果条件恶化请考虑部分退出。',

    // Common
    'common.loading': '加载指标中...',
    'common.error': '错误',
    'common.success': '成功',
    'common.warning': '警告',
    'common.info': '信息'
  }
};

// Current language state
let currentLanguage = 'en';

// Language change listeners
const languageChangeListeners = new Set();

/**
 * Get current language
 */
export function getCurrentLanguage() {
  return currentLanguage;
}

/**
 * Set language and notify listeners
 */
export function setLanguage(lang) {
  if (translations[lang]) {
    currentLanguage = lang;
    localStorage.setItem('preferred-language', lang);
    
    // Notify all listeners
    languageChangeListeners.forEach(listener => listener(lang));
    
    // Update document language
    document.documentElement.lang = lang;
  }
}

/**
 * Get translation for a key
 */
export function t(key, params = {}) {
  const translation = translations[currentLanguage]?.[key] || translations.en[key] || key;
  
  // Replace parameters in translation
  return Object.keys(params).reduce((str, param) => {
    return str.replace(`{${param}}`, params[param]);
  }, translation);
}

/**
 * Add language change listener
 */
export function onLanguageChange(listener) {
  languageChangeListeners.add(listener);
  return () => languageChangeListeners.delete(listener);
}

/**
 * Initialize language from localStorage or browser
 */
export function initializeLanguage() {
  const saved = localStorage.getItem('preferred-language');
  const browserLang = navigator.language.startsWith('zh') ? 'zh' : 'en';
  const lang = saved || browserLang;
  
  setLanguage(lang);
}

/**
 * Get available languages
 */
export function getAvailableLanguages() {
  return [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'zh', name: '中文', flag: '🇨🇳' }
  ];
}

// Initialize on module load
initializeLanguage();
